import { type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shirt } from 'lucide-react';
import { useApiClient } from '../../lib/api/api-context.js';
import { getReadUrl } from '../../lib/media/media-api.js';

/**
 * The design reference photograph on a card.
 *
 * The board sends a media ID, not a URL, because read URLs are signed and live about two
 * minutes while a board is left open all morning. So the URL is fetched per card that is
 * actually rendered, cached by React Query for a minute, and dropped when the card unmounts.
 * The garment icon stands in until it arrives — and permanently for the many garments a shop
 * never photographs, which must not look like a broken image.
 */
export function CardThumbnail({
  mediaId,
  alt,
}: {
  mediaId: string | null;
  alt: string;
}): ReactElement {
  const client = useApiClient();

  const readUrl = useQuery({
    queryKey: ['media', mediaId, 'url', 'thumb'],
    queryFn: () => getReadUrl(client, mediaId as string, 'thumb'),
    enabled: mediaId !== null,
    gcTime: 0,
    staleTime: 60_000,
    retry: 1,
  });

  if (mediaId && readUrl.data) {
    return (
      <img
        src={readUrl.data.url}
        alt={alt}
        loading="lazy"
        className="size-12 shrink-0 rounded-md object-cover"
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className="flex size-12 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
    >
      <Shirt className="size-5" />
    </span>
  );
}
